import { useState, useEffect, useRef } from 'react';
import { Send, User, Bell, MapPin, ChevronRight, X, Loader2, CheckCircle, Home, Calendar } from 'lucide-react';
import { supabaseClient as supabase } from '../../lib/supabase/index';
import MessageFormatter from './MessageFormatter';
import { stayService } from '../../lib/stay/stayService';
import { markReceptionMessagesAsRead } from '../../hooks/useUnreadReceptionMessages';

function toDateInputValue(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

function getStayFallbackDates(): { check_in_date: string; check_out_date: string } {
  const dates = stayService.getStayDates();
  if (!dates) return { check_in_date: '', check_out_date: '' };
  return {
    check_in_date: toDateInputValue(new Date(dates.startDate)),
    check_out_date: toDateInputValue(new Date(dates.endDate)),
  };
}

interface ChatConversation {
  id: string;
  hotel_id: string;
  status: string;
  created_at: string;
  user_name?: string | null;
  user_email?: string | null;
  user_phone?: string | null;
  room_number?: string | null;
  check_in_date?: string | null;
  check_out_date?: string | null;
}

interface ChatMessage {
  id: string;
  conversation_id: string;
  role: 'user' | 'assistant';
  content: string;
  created_at: string;
  metadata?: Record<string, unknown> | null;
}

type Scope = 'hotel' | 'around';

interface ChatWidgetProps {
  hotelId: string;
  hotelName?: string;
  language?: string;
  majorColor?: string;
  receptionPhone?: string;
  allowReceptionChat?: boolean;
}

const SUPPORTED_LANGUAGES = [
  { code: 'fr', label: 'Français', flag: '🇫🇷' },
  { code: 'en', label: 'English', flag: '🇬🇧' },
  { code: 'de', label: 'Deutsch', flag: '🇩🇪' },
  { code: 'es', label: 'Español', flag: '🇪🇸' },
  { code: 'it', label: 'Italiano', flag: '🇮🇹' },
  { code: 'pt', label: 'Português', flag: '🇵🇹' },
  { code: 'nl', label: 'Nederlands', flag: '🇳🇱' },
  { code: 'ru', label: 'Русский', flag: '🇷🇺' },
  { code: 'ja', label: '日本語', flag: '🇯🇵' },
  { code: 'zh', label: '中文', flag: '🇨🇳' },
];

const i18n: Record<string, {
  welcome: string;
  hotelScope: string;
  aroundScope: string;
  hotelScopeDesc: string;
  aroundScopeDesc: string;
  placeholder: string;
  deleteTitle: string;
  deleteBody: string;
  cancel: string;
  delete: string;
  errorMsg: string;
  source: string;
  contactReception: string;
  contactReceptionDesc: string;
  receptionFormTitle: string;
  receptionFormDesc: string;
  receptionFormEmail: string;
  receptionFormEmailPlaceholder: string;
  receptionFormMessage: string;
  receptionFormMessagePlaceholder: string;
  receptionFormSubmit: string;
  receptionFormCancel: string;
  receptionFormSuccess: string;
  receptionFormSuccessDesc: string;
  receptionFormDelayNotice: string;
  receptionFormEmailRequired: string;
  receptionFormName: string;
  receptionFormNamePlaceholder: string;
  receptionFormStatus: string;
  receptionFormExternalVisitor: string;
  receptionFormHotelResident: string;
  receptionFormStay: string;
  receptionFormRoomNumber: string;
  receptionFormRoomPlaceholder: string;
  receptionFormCheckIn: string;
  receptionFormCheckOut: string;
  profileTitle: string;
  profilePrompt: string;
  profileName: string;
  profileNamePlaceholder: string;
  profileEmail: string;
  profilePhone: string;
  profilePhonePlaceholder: string;
  profileStay: string;
  profileRoomNumber: string;
  profileRoomPlaceholder: string;
  profileCheckIn: string;
  profileCheckOut: string;
  profileSave: string;
  profileCancel: string;
  profileButtonTitle: string;
}> = {
  fr: {
    welcome: "Bonjour ! Je suis votre assistant virtuel. Comment puis-je vous aider aujourd'hui ?",
    hotelScope: 'Infos Hôtel',
    aroundScope: 'Aux Alentours',
    hotelScopeDesc: "FAQ et services de l'hôtel",
    aroundScopeDesc: 'Restaurants, activités, lieux à proximité',
    placeholder: 'Posez votre question...',
    deleteTitle: 'Supprimer la conversation ?',
    deleteBody: 'Tous les messages seront supprimés définitivement.',
    cancel: 'Annuler',
    delete: 'Supprimer',
    errorMsg: "Désolé, une erreur s'est produite. Veuillez réessayer.",
    source: 'Source',
    contactReception: 'Contacter la réception',
    contactReceptionDesc: 'Envoyez un message à notre équipe, nous vous répondrons par email.',
    receptionFormTitle: 'Message à la réception',
    receptionFormDesc: 'Notre équipe vous répondra par email. La réponse peut prendre quelques heures.',
    receptionFormEmail: 'Votre email *',
    receptionFormEmailPlaceholder: 'votre@email.com',
    receptionFormMessage: 'Votre message *',
    receptionFormMessagePlaceholder: 'Décrivez votre question ou demande...',
    receptionFormSubmit: 'Envoyer le message',
    receptionFormCancel: 'Annuler',
    receptionFormSuccess: 'Message envoyé !',
    receptionFormSuccessDesc: 'La réception a bien reçu votre message et vous répondra à l\'adresse email indiquée. La réponse peut prendre quelques heures.',
    receptionFormDelayNotice: 'La réponse peut prendre quelques heures. Laissez votre email pour recevoir la réponse même si vous n\'êtes plus connecté.',
    receptionFormEmailRequired: 'L\'email est requis pour recevoir la réponse.',
    receptionFormName: 'Votre nom',
    receptionFormNamePlaceholder: 'Votre nom',
    receptionFormStatus: 'Statut',
    receptionFormExternalVisitor: 'Visiteur extérieur',
    receptionFormHotelResident: 'Résident hôtel',
    receptionFormStay: 'Séjour',
    receptionFormRoomNumber: 'Numéro de chambre',
    receptionFormRoomPlaceholder: 'ex : 214',
    receptionFormCheckIn: 'Arrivée',
    receptionFormCheckOut: 'Départ',
    profileTitle: 'Mes informations',
    profilePrompt: 'Renseignez votre profil pour une aide personnalisée',
    profileName: 'Nom',
    profileNamePlaceholder: 'Votre nom',
    profileEmail: 'Email',
    profilePhone: 'Téléphone',
    profilePhonePlaceholder: '+33 6 00 00 00 00',
    profileStay: 'Séjour',
    profileRoomNumber: 'Numéro de chambre',
    profileRoomPlaceholder: 'ex : 214',
    profileCheckIn: 'Arrivée',
    profileCheckOut: 'Départ',
    profileSave: 'Enregistrer',
    profileCancel: 'Annuler',
    profileButtonTitle: 'Mon profil',
  },
  en: {
    welcome: 'Hello! I am your virtual assistant. How can I help you today?',
    hotelScope: 'Hotel Info',
    aroundScope: 'Around Us',
    hotelScopeDesc: 'Hotel FAQ and services',
    aroundScopeDesc: 'Restaurants, activities, nearby places',
    placeholder: 'Ask your question...',
    deleteTitle: 'Delete conversation?',
    deleteBody: 'All messages will be permanently deleted.',
    cancel: 'Cancel',
    delete: 'Delete',
    errorMsg: 'Sorry, an error occurred. Please try again.',
    source: 'Source',
    contactReception: 'Contact Reception',
    contactReceptionDesc: 'Send a message to our team and we\'ll reply by email.',
    receptionFormTitle: 'Message to Reception',
    receptionFormDesc: 'Our team will reply by email. Response may take a few hours.',
    receptionFormEmail: 'Your email *',
    receptionFormEmailPlaceholder: 'your@email.com',
    receptionFormMessage: 'Your message *',
    receptionFormMessagePlaceholder: 'Describe your question or request...',
    receptionFormSubmit: 'Send message',
    receptionFormCancel: 'Cancel',
    receptionFormSuccess: 'Message sent!',
    receptionFormSuccessDesc: 'The reception team has received your message and will reply to the email address you provided. Response may take a few hours.',
    receptionFormDelayNotice: 'Response may take a few hours. Leave your email to receive the reply even if you\'re no longer connected.',
    receptionFormEmailRequired: 'Email is required to receive the reply.',
    receptionFormName: 'Your name',
    receptionFormNamePlaceholder: 'Your name',
    receptionFormStatus: 'Status',
    receptionFormExternalVisitor: 'External visitor',
    receptionFormHotelResident: 'Hotel guest',
    receptionFormStay: 'Stay',
    receptionFormRoomNumber: 'Room number',
    receptionFormRoomPlaceholder: 'e.g. 214',
    receptionFormCheckIn: 'Check-in',
    receptionFormCheckOut: 'Check-out',
    profileTitle: 'My information',
    profilePrompt: 'Fill in your profile for personalised assistance',
    profileName: 'Name',
    profileNamePlaceholder: 'Your name',
    profileEmail: 'Email',
    profilePhone: 'Phone',
    profilePhonePlaceholder: '+1 000 000 0000',
    profileStay: 'Stay',
    profileRoomNumber: 'Room number',
    profileRoomPlaceholder: 'e.g. 214',
    profileCheckIn: 'Check-in',
    profileCheckOut: 'Check-out',
    profileSave: 'Save',
    profileCancel: 'Cancel',
    profileButtonTitle: 'My profile',
  },
  de: {
    welcome: 'Guten Tag! Ich bin Ihr virtueller Assistent. Wie kann ich Ihnen helfen?',
    hotelScope: 'Hotel-Infos',
    aroundScope: 'In der Umgebung',
    hotelScopeDesc: 'Hotel-FAQ und -Services',
    aroundScopeDesc: 'Restaurants, Aktivitäten, nahegelegene Orte',
    placeholder: 'Stellen Sie Ihre Frage...',
    deleteTitle: 'Gespräch löschen?',
    deleteBody: 'Alle Nachrichten werden dauerhaft gelöscht.',
    cancel: 'Abbrechen',
    delete: 'Löschen',
    errorMsg: 'Entschuldigung, ein Fehler ist aufgetreten. Bitte versuchen Sie es erneut.',
    source: 'Quelle',
    contactReception: 'Rezeption kontaktieren',
    contactReceptionDesc: 'Senden Sie eine Nachricht an unser Team, wir antworten per E-Mail.',
    receptionFormTitle: 'Nachricht an die Rezeption',
    receptionFormDesc: 'Unser Team antwortet per E-Mail. Die Antwort kann einige Stunden dauern.',
    receptionFormEmail: 'Ihre E-Mail *',
    receptionFormEmailPlaceholder: 'ihre@email.de',
    receptionFormMessage: 'Ihre Nachricht *',
    receptionFormMessagePlaceholder: 'Beschreiben Sie Ihre Frage oder Anfrage...',
    receptionFormSubmit: 'Nachricht senden',
    receptionFormCancel: 'Abbrechen',
    receptionFormSuccess: 'Nachricht gesendet!',
    receptionFormSuccessDesc: 'Die Rezeption hat Ihre Nachricht erhalten und wird an Ihre E-Mail-Adresse antworten.',
    receptionFormDelayNotice: 'Die Antwort kann einige Stunden dauern. Hinterlassen Sie Ihre E-Mail, um die Antwort zu erhalten.',
    receptionFormEmailRequired: 'E-Mail ist erforderlich, um die Antwort zu erhalten.',
    receptionFormName: 'Ihr Name',
    receptionFormNamePlaceholder: 'Ihr Name',
    receptionFormStatus: 'Status',
    receptionFormExternalVisitor: 'Externer Besucher',
    receptionFormHotelResident: 'Hotelgast',
    receptionFormStay: 'Aufenthalt',
    receptionFormRoomNumber: 'Zimmernummer',
    receptionFormRoomPlaceholder: 'z.B. 214',
    receptionFormCheckIn: 'Anreise',
    receptionFormCheckOut: 'Abreise',
    profileTitle: 'Meine Informationen',
    profilePrompt: 'Füllen Sie Ihr Profil für persönliche Hilfe aus',
    profileName: 'Name',
    profileNamePlaceholder: 'Ihr Name',
    profileEmail: 'E-Mail',
    profilePhone: 'Telefon',
    profilePhonePlaceholder: '+49 000 000000',
    profileStay: 'Aufenthalt',
    profileRoomNumber: 'Zimmernummer',
    profileRoomPlaceholder: 'z.B. 214',
    profileCheckIn: 'Anreise',
    profileCheckOut: 'Abreise',
    profileSave: 'Speichern',
    profileCancel: 'Abbrechen',
    profileButtonTitle: 'Mein Profil',
  },
  es: {
    welcome: '¡Hola! Soy su asistente virtual. ¿En qué puedo ayudarle hoy?',
    hotelScope: 'Info Hotel',
    aroundScope: 'Alrededores',
    hotelScopeDesc: 'FAQ y servicios del hotel',
    aroundScopeDesc: 'Restaurantes, actividades, lugares cercanos',
    placeholder: 'Haga su pregunta...',
    deleteTitle: '¿Eliminar la conversación?',
    deleteBody: 'Todos los mensajes se eliminarán definitivamente.',
    cancel: 'Cancelar',
    delete: 'Eliminar',
    errorMsg: 'Lo sentimos, se ha producido un error. Por favor, inténtelo de nuevo.',
    source: 'Fuente',
    contactReception: 'Contactar Recepción',
    contactReceptionDesc: 'Envíe un mensaje a nuestro equipo y le responderemos por email.',
    receptionFormTitle: 'Mensaje a Recepción',
    receptionFormDesc: 'Nuestro equipo le responderá por email. La respuesta puede tardar unas horas.',
    receptionFormEmail: 'Su email *',
    receptionFormEmailPlaceholder: 'su@email.com',
    receptionFormMessage: 'Su mensaje *',
    receptionFormMessagePlaceholder: 'Describa su pregunta o solicitud...',
    receptionFormSubmit: 'Enviar mensaje',
    receptionFormCancel: 'Cancelar',
    receptionFormSuccess: '¡Mensaje enviado!',
    receptionFormSuccessDesc: 'La recepción ha recibido su mensaje y le responderá al email indicado.',
    receptionFormDelayNotice: 'La respuesta puede tardar unas horas. Deje su email para recibir la respuesta.',
    receptionFormEmailRequired: 'El email es necesario para recibir la respuesta.',
    receptionFormName: 'Su nombre',
    receptionFormNamePlaceholder: 'Su nombre',
    receptionFormStatus: 'Estado',
    receptionFormExternalVisitor: 'Visitante externo',
    receptionFormHotelResident: 'Huésped del hotel',
    receptionFormStay: 'Estancia',
    receptionFormRoomNumber: 'Número de habitación',
    receptionFormRoomPlaceholder: 'ej. 214',
    receptionFormCheckIn: 'Llegada',
    receptionFormCheckOut: 'Salida',
    profileTitle: 'Mis datos',
    profilePrompt: 'Complete su perfil para una ayuda personalizada',
    profileName: 'Nombre',
    profileNamePlaceholder: 'Su nombre',
    profileEmail: 'Email',
    profilePhone: 'Teléfono',
    profilePhonePlaceholder: '+34 000 000 000',
    profileStay: 'Estancia',
    profileRoomNumber: 'Número de habitación',
    profileRoomPlaceholder: 'ej: 214',
    profileCheckIn: 'Llegada',
    profileCheckOut: 'Salida',
    profileSave: 'Guardar',
    profileCancel: 'Cancelar',
    profileButtonTitle: 'Mi perfil',
  },
  it: {
    welcome: 'Buongiorno! Sono il vostro assistente virtuale. Come posso aiutarvi oggi?',
    hotelScope: 'Info Hotel',
    aroundScope: 'Nei Dintorni',
    hotelScopeDesc: "FAQ e servizi dell'hotel",
    aroundScopeDesc: 'Ristoranti, attività, luoghi vicini',
    placeholder: 'Fai la tua domanda...',
    deleteTitle: 'Eliminare la conversazione?',
    deleteBody: 'Tutti i messaggi saranno eliminati definitivamente.',
    cancel: 'Annulla',
    delete: 'Elimina',
    errorMsg: "Siamo spiacenti, si è verificato un errore. Si prega di riprovare.",
    source: 'Fonte',
    contactReception: 'Contatta la Reception',
    contactReceptionDesc: 'Invia un messaggio al nostro team, ti risponderemo via email.',
    receptionFormTitle: 'Messaggio alla Reception',
    receptionFormDesc: 'Il nostro team ti risponderà via email. La risposta può richiedere alcune ore.',
    receptionFormEmail: 'La tua email *',
    receptionFormEmailPlaceholder: 'tua@email.it',
    receptionFormMessage: 'Il tuo messaggio *',
    receptionFormMessagePlaceholder: 'Descrivi la tua domanda o richiesta...',
    receptionFormSubmit: 'Invia messaggio',
    receptionFormCancel: 'Annulla',
    receptionFormSuccess: 'Messaggio inviato!',
    receptionFormSuccessDesc: 'La reception ha ricevuto il tuo messaggio e risponderà all\'email indicata.',
    receptionFormDelayNotice: 'La risposta può richiedere alcune ore. Lascia la tua email per ricevere la risposta.',
    receptionFormEmailRequired: 'L\'email è necessaria per ricevere la risposta.',
    receptionFormName: 'Il tuo nome',
    receptionFormNamePlaceholder: 'Il tuo nome',
    receptionFormStatus: 'Stato',
    receptionFormExternalVisitor: 'Visitatore esterno',
    receptionFormHotelResident: 'Ospite dell\'hotel',
    receptionFormStay: 'Soggiorno',
    receptionFormRoomNumber: 'Numero di camera',
    receptionFormRoomPlaceholder: 'es. 214',
    receptionFormCheckIn: 'Arrivo',
    receptionFormCheckOut: 'Partenza',
    profileTitle: 'Le mie informazioni',
    profilePrompt: 'Compila il tuo profilo per un\'assistenza personalizzata',
    profileName: 'Nome',
    profileNamePlaceholder: 'Il tuo nome',
    profileEmail: 'Email',
    profilePhone: 'Telefono',
    profilePhonePlaceholder: '+39 000 000 0000',
    profileStay: 'Soggiorno',
    profileRoomNumber: 'Numero di camera',
    profileRoomPlaceholder: 'es: 214',
    profileCheckIn: 'Arrivo',
    profileCheckOut: 'Partenza',
    profileSave: 'Salva',
    profileCancel: 'Annulla',
    profileButtonTitle: 'Il mio profilo',
  },
  pt: {
    welcome: 'Olá! Sou o seu assistente virtual. Como posso ajudá-lo hoje?',
    hotelScope: 'Info Hotel',
    aroundScope: 'Arredores',
    hotelScopeDesc: 'FAQ e serviços do hotel',
    aroundScopeDesc: 'Restaurantes, atividades, locais próximos',
    placeholder: 'Faça a sua pergunta...',
    deleteTitle: 'Eliminar a conversa?',
    deleteBody: 'Todas as mensagens serão eliminadas definitivamente.',
    cancel: 'Cancelar',
    delete: 'Eliminar',
    errorMsg: 'Lamentamos, ocorreu um erro. Por favor, tente novamente.',
    source: 'Fonte',
    contactReception: 'Contactar Receção',
    contactReceptionDesc: 'Envie uma mensagem à nossa equipa, responderemos por email.',
    receptionFormTitle: 'Mensagem para a Receção',
    receptionFormDesc: 'A nossa equipa responderá por email. A resposta pode demorar algumas horas.',
    receptionFormEmail: 'O seu email *',
    receptionFormEmailPlaceholder: 'seu@email.pt',
    receptionFormMessage: 'A sua mensagem *',
    receptionFormMessagePlaceholder: 'Descreva a sua pergunta ou pedido...',
    receptionFormSubmit: 'Enviar mensagem',
    receptionFormCancel: 'Cancelar',
    receptionFormSuccess: 'Mensagem enviada!',
    receptionFormSuccessDesc: 'A receção recebeu a sua mensagem e responderá ao email indicado.',
    receptionFormDelayNotice: 'A resposta pode demorar algumas horas. Deixe o seu email para receber a resposta.',
    receptionFormEmailRequired: 'O email é necessário para receber a resposta.',
    receptionFormName: 'O seu nome',
    receptionFormNamePlaceholder: 'O seu nome',
    receptionFormStatus: 'Estado',
    receptionFormExternalVisitor: 'Visitante externo',
    receptionFormHotelResident: 'Hóspede do hotel',
    receptionFormStay: 'Estadia',
    receptionFormRoomNumber: 'Número do quarto',
    receptionFormRoomPlaceholder: 'ex. 214',
    receptionFormCheckIn: 'Chegada',
    receptionFormCheckOut: 'Saída',
    profileTitle: 'As minhas informações',
    profilePrompt: 'Preencha o seu perfil para uma ajuda personalizada',
    profileName: 'Nome',
    profileNamePlaceholder: 'O seu nome',
    profileEmail: 'Email',
    profilePhone: 'Telefone',
    profilePhonePlaceholder: '+351 000 000 000',
    profileStay: 'Estadia',
    profileRoomNumber: 'Número do quarto',
    profileRoomPlaceholder: 'ex: 214',
    profileCheckIn: 'Chegada',
    profileCheckOut: 'Saída',
    profileSave: 'Guardar',
    profileCancel: 'Cancelar',
    profileButtonTitle: 'O meu perfil',
  },
  nl: {
    welcome: 'Hallo! Ik ben uw virtuele assistent. Hoe kan ik u vandaag helpen?',
    hotelScope: 'Hotel Info',
    aroundScope: 'In de Buurt',
    hotelScopeDesc: 'Hotel FAQ en services',
    aroundScopeDesc: "Restaurants, activiteiten, nabijgelegen plaatsen",
    placeholder: 'Stel uw vraag...',
    deleteTitle: 'Gesprek verwijderen?',
    deleteBody: 'Alle berichten worden permanent verwijderd.',
    cancel: 'Annuleren',
    delete: 'Verwijderen',
    errorMsg: 'Sorry, er is een fout opgetreden. Probeer het opnieuw.',
    source: 'Bron',
    contactReception: 'Contact Receptie',
    contactReceptionDesc: 'Stuur een bericht naar ons team, we antwoorden per e-mail.',
    receptionFormTitle: 'Bericht aan Receptie',
    receptionFormDesc: 'Ons team antwoordt per e-mail. Reactie kan enkele uren duren.',
    receptionFormEmail: 'Uw e-mail *',
    receptionFormEmailPlaceholder: 'uw@email.nl',
    receptionFormMessage: 'Uw bericht *',
    receptionFormMessagePlaceholder: 'Beschrijf uw vraag of verzoek...',
    receptionFormSubmit: 'Bericht verzenden',
    receptionFormCancel: 'Annuleren',
    receptionFormSuccess: 'Bericht verzonden!',
    receptionFormSuccessDesc: 'De receptie heeft uw bericht ontvangen en antwoordt op het opgegeven e-mailadres.',
    receptionFormDelayNotice: 'Reactie kan enkele uren duren. Laat uw e-mail achter om het antwoord te ontvangen.',
    receptionFormEmailRequired: 'E-mail is vereist om het antwoord te ontvangen.',
    receptionFormName: 'Uw naam',
    receptionFormNamePlaceholder: 'Uw naam',
    receptionFormStatus: 'Status',
    receptionFormExternalVisitor: 'Externe bezoeker',
    receptionFormHotelResident: 'Hotelgast',
    receptionFormStay: 'Verblijf',
    receptionFormRoomNumber: 'Kamernummer',
    receptionFormRoomPlaceholder: 'bijv. 214',
    receptionFormCheckIn: 'Aankomst',
    receptionFormCheckOut: 'Vertrek',
    profileTitle: 'Mijn gegevens',
    profilePrompt: 'Vul uw profiel in voor persoonlijke hulp',
    profileName: 'Naam',
    profileNamePlaceholder: 'Uw naam',
    profileEmail: 'E-mail',
    profilePhone: 'Telefoon',
    profilePhonePlaceholder: '+31 000 000 0000',
    profileStay: 'Verblijf',
    profileRoomNumber: 'Kamernummer',
    profileRoomPlaceholder: 'bijv. 214',
    profileCheckIn: 'Aankomst',
    profileCheckOut: 'Vertrek',
    profileSave: 'Opslaan',
    profileCancel: 'Annuleren',
    profileButtonTitle: 'Mijn profiel',
  },
  ru: {
    welcome: 'Здравствуйте! Я ваш виртуальный помощник. Чем могу помочь?',
    hotelScope: 'Об отеле',
    aroundScope: 'Вокруг отеля',
    hotelScopeDesc: 'FAQ и услуги отеля',
    aroundScopeDesc: 'Рестораны, мероприятия, ближайшие места',
    placeholder: 'Задайте вопрос...',
    deleteTitle: 'Удалить переписку?',
    deleteBody: 'Все сообщения будут удалены безвозвратно.',
    cancel: 'Отмена',
    delete: 'Удалить',
    errorMsg: 'Извините, произошла ошибка. Пожалуйста, попробуйте ещё раз.',
    source: 'Источник',
    contactReception: 'Написать на ресепшн',
    contactReceptionDesc: 'Отправьте сообщение нашей команде, мы ответим по email.',
    receptionFormTitle: 'Сообщение на ресепшн',
    receptionFormDesc: 'Наша команда ответит по email. Ответ может занять несколько часов.',
    receptionFormEmail: 'Ваш email *',
    receptionFormEmailPlaceholder: 'ваш@email.com',
    receptionFormMessage: 'Ваше сообщение *',
    receptionFormMessagePlaceholder: 'Опишите ваш вопрос или запрос...',
    receptionFormSubmit: 'Отправить сообщение',
    receptionFormCancel: 'Отмена',
    receptionFormSuccess: 'Сообщение отправлено!',
    receptionFormSuccessDesc: 'Ресепшн получил ваше сообщение и ответит на указанный email.',
    receptionFormDelayNotice: 'Ответ может занять несколько часов. Оставьте email, чтобы получить ответ.',
    receptionFormEmailRequired: 'Email необходим для получения ответа.',
    receptionFormName: 'Ваше имя',
    receptionFormNamePlaceholder: 'Ваше имя',
    receptionFormStatus: 'Статус',
    receptionFormExternalVisitor: 'Внешний посетитель',
    receptionFormHotelResident: 'Гость отеля',
    receptionFormStay: 'Пребывание',
    receptionFormRoomNumber: 'Номер комнаты',
    receptionFormRoomPlaceholder: 'напр. 214',
    receptionFormCheckIn: 'Заезд',
    receptionFormCheckOut: 'Выезд',
    profileTitle: 'Мои данные',
    profilePrompt: 'Заполните профиль для персонализированной помощи',
    profileName: 'Имя',
    profileNamePlaceholder: 'Ваше имя',
    profileEmail: 'Email',
    profilePhone: 'Телефон',
    profilePhonePlaceholder: '+7 000 000 0000',
    profileStay: 'Проживание',
    profileRoomNumber: 'Номер комнаты',
    profileRoomPlaceholder: 'напр. 214',
    profileCheckIn: 'Заезд',
    profileCheckOut: 'Выезд',
    profileSave: 'Сохранить',
    profileCancel: 'Отмена',
    profileButtonTitle: 'Мой профиль',
  },
  ja: {
    welcome: 'こんにちは！バーチャルアシスタントです。本日はどのようなご用件でしょうか？',
    hotelScope: 'ホテル情報',
    aroundScope: '周辺情報',
    hotelScopeDesc: 'ホテルのFAQとサービス',
    aroundScopeDesc: 'レストラン、アクティビティ、近くの場所',
    placeholder: 'ご質問をどうぞ...',
    deleteTitle: '会話を削除しますか？',
    deleteBody: 'すべてのメッセージが完全に削除されます。',
    cancel: 'キャンセル',
    delete: '削除',
    errorMsg: '申し訳ありませんが、エラーが発生しました。もう一度お試しください。',
    source: 'ソース',
    contactReception: 'フロントに連絡',
    contactReceptionDesc: 'チームにメッセージを送ると、メールで返信いたします。',
    receptionFormTitle: 'フロントへのメッセージ',
    receptionFormDesc: 'チームがメールで返信します。返信には数時間かかる場合があります。',
    receptionFormEmail: 'メールアドレス *',
    receptionFormEmailPlaceholder: 'your@email.com',
    receptionFormMessage: 'メッセージ *',
    receptionFormMessagePlaceholder: 'ご質問やご要望を記入してください...',
    receptionFormSubmit: 'メッセージを送信',
    receptionFormCancel: 'キャンセル',
    receptionFormSuccess: 'メッセージを送信しました！',
    receptionFormSuccessDesc: 'フロントがメッセージを受け取りました。ご提供いただいたメールアドレスに返信いたします。',
    receptionFormDelayNotice: '返信には数時間かかる場合があります。メールアドレスを入力してください。',
    receptionFormEmailRequired: '返信を受け取るにはメールアドレスが必要です。',
    receptionFormName: 'お名前',
    receptionFormNamePlaceholder: 'お名前',
    receptionFormStatus: 'ステータス',
    receptionFormExternalVisitor: '外部訪問者',
    receptionFormHotelResident: 'ホテルゲスト',
    receptionFormStay: '滞在',
    receptionFormRoomNumber: '部屋番号',
    receptionFormRoomPlaceholder: '例：214',
    receptionFormCheckIn: 'チェックイン',
    receptionFormCheckOut: 'チェックアウト',
    profileTitle: '私の情報',
    profilePrompt: 'プロフィールを入力してパーソナライズされたサポートを受けましょう',
    profileName: '名前',
    profileNamePlaceholder: 'お名前',
    profileEmail: 'メールアドレス',
    profilePhone: '電話番号',
    profilePhonePlaceholder: '+81 00 0000 0000',
    profileStay: '滞在情報',
    profileRoomNumber: '部屋番号',
    profileRoomPlaceholder: '例：214',
    profileCheckIn: 'チェックイン',
    profileCheckOut: 'チェックアウト',
    profileSave: '保存',
    profileCancel: 'キャンセル',
    profileButtonTitle: 'マイプロフィール',
  },
  zh: {
    welcome: '您好！我是您的虚拟助手。今天我能为您提供什么帮助？',
    hotelScope: '酒店信息',
    aroundScope: '周边推荐',
    hotelScopeDesc: '酒店常见问题和服务',
    aroundScopeDesc: '餐厅、活动、附近地点',
    placeholder: '请输入您的问题...',
    deleteTitle: '删除对话？',
    deleteBody: '所有消息将被永久删除。',
    cancel: '取消',
    delete: '删除',
    errorMsg: '抱歉，发生了错误。请重试。',
    source: '来源',
    contactReception: '联系前台',
    contactReceptionDesc: '向我们的团队发送消息，我们将通过电子邮件回复。',
    receptionFormTitle: '发送消息给前台',
    receptionFormDesc: '我们的团队将通过电子邮件回复。回复可能需要几个小时。',
    receptionFormEmail: '您的邮箱 *',
    receptionFormEmailPlaceholder: 'your@email.com',
    receptionFormMessage: '您的消息 *',
    receptionFormMessagePlaceholder: '描述您的问题或请求...',
    receptionFormSubmit: '发送消息',
    receptionFormCancel: '取消',
    receptionFormSuccess: '消息已发送！',
    receptionFormSuccessDesc: '前台已收到您的消息，将回复到您提供的邮箱地址。',
    receptionFormDelayNotice: '回复可能需要几个小时。请留下您的邮箱以便接收回复。',
    receptionFormEmailRequired: '接收回复需要提供邮箱地址。',
    receptionFormName: '您的姓名',
    receptionFormNamePlaceholder: '您的姓名',
    receptionFormStatus: '状态',
    receptionFormExternalVisitor: '外部访客',
    receptionFormHotelResident: '酒店客人',
    receptionFormStay: '住宿',
    receptionFormRoomNumber: '房间号',
    receptionFormRoomPlaceholder: '例：214',
    receptionFormCheckIn: '入住',
    receptionFormCheckOut: '退房',
    profileTitle: '我的信息',
    profilePrompt: '填写您的个人资料以获得个性化帮助',
    profileName: '姓名',
    profileNamePlaceholder: '您的姓名',
    profileEmail: '邮箱',
    profilePhone: '电话',
    profilePhonePlaceholder: '+86 000 0000 0000',
    profileStay: '住宿信息',
    profileRoomNumber: '房间号',
    profileRoomPlaceholder: '例：214',
    profileCheckIn: '入住',
    profileCheckOut: '退房',
    profileSave: '保存',
    profileCancel: '取消',
    profileButtonTitle: '我的档案',
  },
};

function getT(lang: string) {
  return i18n[lang] ?? i18n['fr'];
}

function getStoredLanguage(): string {
  return localStorage.getItem('chatbot_language') || 'fr';
}

interface ReceptionFormState {
  messageId: string;
  originalQuestion: string;
  show: boolean;
  submitted: boolean;
  submitting: boolean;
  guest_name: string;
  email: string;
  message: string;
  emailError: string;
  isResident: boolean;
  room_number: string;
  check_in_date: string;
  check_out_date: string;
}

export default function ChatWidget({ hotelId, hotelName, language: propLanguage, majorColor, receptionPhone, allowReceptionChat }: ChatWidgetProps) {
  const THEME = {
    bg: '#414143',
    major: '#6B7280',
    dark: '#1F2937',
    medium: '#4B5563',
    light: '#F3F4F6'
  } as const;

  const rawColor = majorColor || '#F59E0B';
  const MAJOR_GOLD = rawColor;
  const MAJOR_GOLD_SOFT = `${rawColor}22`;
  const MAJOR_GOLD_RING = `${rawColor}55`;

  const [conversation, setConversation] = useState<ChatConversation | null>(null);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [showUserInfoModal, setShowUserInfoModal] = useState(false);
  const [scope, setScope] = useState<Scope>('hotel');
  const [language, setLanguage] = useState<string>(propLanguage ?? getStoredLanguage());
  const [receptionForm, setReceptionForm] = useState<ReceptionFormState | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const t = getT(language);

  useEffect(() => {
    if (messages.some(m => m.role === 'reception')) {
      markReceptionMessagesAsRead();
    }
  }, [messages]);

  useEffect(() => {
    if (propLanguage) {
      setLanguage(propLanguage);
    }
  }, [propLanguage]);

  useEffect(() => {
    const savedScope = localStorage.getItem(`chat_scope_${hotelId}`);
    if (savedScope && (savedScope === 'hotel' || savedScope === 'around')) {
      setScope(savedScope as Scope);
    }
  }, [hotelId]);

  const handleScopeChange = (newScope: Scope) => {
    setScope(newScope);
    localStorage.setItem(`chat_scope_${hotelId}`, newScope);
  };

  const handleLanguageChange = (code: string) => {
    setLanguage(code);
    localStorage.setItem('chatbot_language', code);
    setShowLangPicker(false);
  };

  useEffect(() => {
    initializeConversation();
  }, [hotelId]);

  useEffect(() => {
    if (conversation?.id) {
      loadMessages();
      const cleanup = subscribeToMessages();
      return cleanup;
    }
  }, [conversation?.id]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const initializeConversation = async () => {
    const existingConvId =
      sessionStorage.getItem(`chat_conversation_${hotelId}`) ||
      localStorage.getItem(`chat_conversation_${hotelId}`);

    if (existingConvId) {
      const { data } = await supabase
        .from('chatbot_conversations')
        .select('*')
        .eq('id', existingConvId)
        .maybeSingle();

      if (data) {
        sessionStorage.setItem(`chat_conversation_${hotelId}`, data.id);
        localStorage.setItem(`chat_conversation_${hotelId}`, data.id);
        setConversation(data);
        return;
      }
    }

    const { data, error } = await supabase
      .from('chatbot_conversations')
      .insert({
        hotel_id: hotelId,
        status: 'active'
      })
      .select()
      .single();

    if (error) {
      console.error('Error creating conversation:', error);
      return;
    }

    setConversation(data);
    sessionStorage.setItem(`chat_conversation_${hotelId}`, data.id);
    localStorage.setItem(`chat_conversation_${hotelId}`, data.id);
    window.dispatchEvent(new CustomEvent('chatbot_conversation_ready', { detail: { hotelId, conversationId: data.id } }));

    const welcomeMsg = getT(language).welcome;
    const { error: msgError } = await supabase
      .from('chatbot_messages')
      .insert({
        conversation_id: data.id,
        role: 'assistant',
        content: welcomeMsg
      });

    if (msgError) {
      console.error('Error creating welcome message:', msgError);
    }
  };

  const loadMessages = async () => {
    if (!conversation?.id) return;

    const { data, error } = await supabase
      .from('chatbot_messages')
      .select('*')
      .eq('conversation_id', conversation.id)
      .order('created_at', { ascending: true });

    if (error) {
      console.error('Error loading messages:', error);
      return;
    }

    setMessages(data || []);
    isInitialLoad.current = false;
  };

  const subscribeToMessages = () => {
    if (!conversation?.id) return () => {};

    const channel = supabase
      .channel(`messages-${conversation.id}-${Date.now()}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'chatbot_messages',
          filter: `conversation_id=eq.${conversation.id}`
        },
        (payload) => {
          setMessages(prev => [...prev, payload.new as ChatMessage]);
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  };

  const isInitialLoad = useRef(true);

  const scrollToBottom = () => {
    if (isInitialLoad.current) {
      messagesEndRef.current?.scrollIntoView({ behavior: 'instant' });
    } else {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const handleSaveUserInfo = async (userInfo: {
    user_name: string;
    user_email: string;
    user_phone: string;
    room_number: string;
    check_in_date: string;
    check_out_date: string;
  }) => {
    if (!conversation?.id) return;

    const { error } = await supabase
      .from('chatbot_conversations')
      .update({
        user_name: userInfo.user_name || null,
        user_email: userInfo.user_email || null,
        user_phone: userInfo.user_phone || null,
        room_number: userInfo.room_number || null,
        check_in_date: userInfo.check_in_date || null,
        check_out_date: userInfo.check_out_date || null
      })
      .eq('id', conversation.id);

    if (error) {
      console.error('Error updating user info:', error);
      return;
    }

    setConversation(prev => prev ? { ...prev, ...userInfo } : null);
  };

  const deleteConversation = async () => {
    if (!conversation?.id) return;

    await supabase
      .from('chatbot_messages')
      .delete()
      .eq('conversation_id', conversation.id);

    await supabase
      .from('chatbot_conversations')
      .delete()
      .eq('id', conversation.id);

    sessionStorage.removeItem(`chat_conversation_${hotelId}`);
    setConversation(null);
    setMessages([]);
    setShowDeleteConfirm(false);

    await initializeConversation();
  };

  const sendUserMessage = async (userMessage: string, opts?: { silentUser?: boolean; overrideScope?: Scope }) => {
    if (!userMessage.trim() || !conversation?.id || loading) return;

    const silentUser = opts?.silentUser === true;
    const scopeToUse = opts?.overrideScope ?? scope;

    setLoading(true);

    try {
      if (!silentUser) {
        const { error: userError } = await supabase
          .from('chatbot_messages')
          .insert({
            conversation_id: conversation.id,
            role: 'user',
            content: userMessage
          });

        if (userError) throw userError;
      }

      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/chatbot-ai`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`
          },
          body: JSON.stringify({
            conversationId: conversation.id,
            message: userMessage,
            hotelId,
            scope: scopeToUse,
            language
          })
        }
      );

      if (!response.ok) {
        throw new Error('Failed to get AI response');
      }

      const data = await response.json();
      const meta = data.metadata || {};

      const { data: insertedMsg, error: aiError } = await supabase
        .from('chatbot_messages')
        .insert({
          conversation_id: conversation.id,
          role: 'assistant',
          content: data.response,
          metadata: meta
        })
        .select()
        .single();

      if (aiError) throw aiError;


    } catch (error) {
      console.error('Error sending message:', error);

      await supabase
        .from('chatbot_messages')
        .insert({
          conversation_id: conversation.id,
          role: 'assistant',
          content: t.errorMsg
        });
    } finally {
      setLoading(false);
    }
  };

  const sendMessage = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!input.trim()) return;
    const msg = input.trim();
    setInput('');
    await sendUserMessage(msg, { silentUser: false });
  };

  const handleAction = async (payload: string) => {
    if (!payload || loading) return;

    if (payload.startsWith('DETAILS:')) {
      await sendUserMessage(payload, { silentUser: true });
      return;
    }

    if (payload.startsWith('SWITCH_SCOPE:')) {
      const parts = payload.split(':');
      if (parts.length >= 3) {
        const targetScope = parts[1] as Scope;
        const originalMessage = decodeURIComponent(parts.slice(2).join(':'));

        setScope(targetScope);
        localStorage.setItem(`chat_scope_${hotelId}`, targetScope);

        await sendUserMessage(originalMessage, { silentUser: false, overrideScope: targetScope });
      }
      return;
    }

    await sendUserMessage(payload, { silentUser: true });
  };

  const submitReceptionMessage = async () => {
    if (!receptionForm || !conversation?.id) return;

    if (!receptionForm.email.trim()) {
      setReceptionForm(prev => prev ? { ...prev, emailError: t.receptionFormEmailRequired } : null);
      return;
    }
    if (!receptionForm.message.trim()) return;

    setReceptionForm(prev => prev ? { ...prev, submitting: true, emailError: '' } : null);

    const { error } = await supabase
      .from('reception_messages')
      .insert({
        hotel_id: hotelId,
        conversation_id: conversation.id,
        guest_name: receptionForm.guest_name.trim() || null,
        guest_email: receptionForm.email.trim(),
        guest_room: receptionForm.isResident ? (receptionForm.room_number || null) : null,
        original_question: receptionForm.originalQuestion,
        message: receptionForm.message.trim(),
        status: 'unread',
      });

    if (error) {
      console.error('Error sending reception message:', error);
      setReceptionForm(prev => prev ? { ...prev, submitting: false } : null);
      return;
    }

    const convUpdates: Record<string, string | null> = {};
    if (receptionForm.guest_name.trim() !== (conversation.user_name || '')) {
      convUpdates.user_name = receptionForm.guest_name.trim() || null;
    }
    if (receptionForm.email !== conversation.user_email) {
      convUpdates.user_email = receptionForm.email.trim();
    }
    if (receptionForm.isResident) {
      if (receptionForm.room_number !== (conversation.room_number || '')) convUpdates.room_number = receptionForm.room_number || null;
      if (receptionForm.check_in_date !== (conversation.check_in_date || '')) convUpdates.check_in_date = receptionForm.check_in_date || null;
      if (receptionForm.check_out_date !== (conversation.check_out_date || '')) convUpdates.check_out_date = receptionForm.check_out_date || null;
    }
    if (Object.keys(convUpdates).length > 0) {
      await supabase.from('chatbot_conversations').update(convUpdates).eq('id', conversation.id);
      setConversation(prev => prev ? { ...prev, ...convUpdates } : null);
    }

    setReceptionForm(prev => prev ? { ...prev, submitting: false, submitted: true } : null);
  };


  return (
    <div
      className="relative flex flex-col h-full overflow-hidden"
      style={{ backgroundColor: 'rgba(255,255,255,0.92)', fontFamily: 'system-ui, -apple-system, sans-serif' }}
    >
      <style>{`
        .chat-major-focus:focus {
          outline: none;
          border-color: ${MAJOR_GOLD};
          box-shadow: 0 0 0 3px ${MAJOR_GOLD_RING};
        }
      `}</style>

      <div className="flex gap-1 p-2 border-b" style={{ backgroundColor: 'rgba(255,255,255,0.95)' }}>
        <button
          onClick={() => handleScopeChange('hotel')}
          className="flex-1 flex items-center justify-center gap-2 px-3 py-2.5 rounded-lg text-sm font-semibold transition-all"
          style={scope === 'hotel'
            ? { backgroundColor: MAJOR_GOLD, color: THEME.dark, boxShadow: '0 2px 8px rgba(0,0,0,0.15)' }
            : { backgroundColor: 'transparent', color: '#6B7280', border: '1.5px solid rgba(0,0,0,0.10)' }}
        >
          <Home className="w-4 h-4" />
          {t.hotelScope}
        </button>
        <button
          onClick={() => handleScopeChange('around')}
          className="flex-1 flex items-center justify-center gap-2 px-3 py-2.5 rounded-lg text-sm font-semibold transition-all"
          style={scope === 'around'
            ? { backgroundColor: MAJOR_GOLD, color: THEME.dark, boxShadow: '0 2px 8px rgba(0,0,0,0.15)' }
            : { backgroundColor: 'transparent', color: '#6B7280', border: '1.5px solid rgba(0,0,0,0.10)' }}
        >
          <MapPin className="w-4 h-4" />
          {t.aroundScope}
        </button>
      </div>

      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {messages.map((message) => {
          if (message.role === 'user' && typeof message.content === 'string' && message.content.startsWith('DETAILS:')) {
            return null;
          }

          const meta = (message.metadata || {}) as Record<string, unknown>;
          const showReceptionCta =
            allowReceptionChat === true &&
            message.role === 'assistant' &&
            meta.receptionMessaging === true &&
            meta.isNoInfo === true;

          return (
            <div key={message.id}>
              <div className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                <div
                  className={`max-w-[80%] rounded-lg px-4 py-2 ${
                    message.role === 'user' ? 'text-gray-900' : 'text-gray-900'
                  }`}
                  style={{
                    backgroundColor: message.role === 'user' ? MAJOR_GOLD : THEME.light,
                    border: message.role === 'assistant' ? '1px solid rgba(0,0,0,0.06)' : undefined
                  }}
                >
                  <MessageFormatter
                    content={message.content}
                    isAssistant={message.role === 'assistant'}
                    onAction={handleAction}
                  />
                  <p className="text-xs mt-1 opacity-70">
                    {new Date(message.created_at).toLocaleTimeString('fr-FR', {
                      hour: '2-digit',
                      minute: '2-digit'
                    })}
                  </p>
                </div>
              </div>

              {showReceptionCta && (
                <div className="flex flex-col items-start gap-2 mt-2">
                  {receptionPhone && (
                    <a
                      href={`tel:${receptionPhone}`}
                      className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-all hover:opacity-90 border"
                      style={{
                        backgroundColor: MAJOR_GOLD_SOFT,
                        borderColor: MAJOR_GOLD,
                        color: THEME.dark
                      }}
                    >
                      <Bell className="w-3.5 h-3.5" style={{ color: MAJOR_GOLD }} />
                      <span>{receptionPhone}</span>
                    </a>
                  )}
                  <button
                    onClick={() => setReceptionForm(prev =>
                      prev?.messageId === message.id
                        ? prev
                        : {
                            messageId: message.id,
                            originalQuestion: messages.findLast(m => m.role === 'user')?.content || '',
                            show: true,
                            submitted: false,
                            submitting: false,
                            guest_name: conversation?.user_name || '',
                            email: conversation?.user_email || '',
                            message: messages.findLast(m => m.role === 'user')?.content || '',
                            emailError: '',
                            isResident: !!(conversation?.room_number || conversation?.check_in_date || conversation?.check_out_date),
                            room_number: conversation?.room_number || '',
                            check_in_date: conversation?.check_in_date || getStayFallbackDates().check_in_date,
                            check_out_date: conversation?.check_out_date || getStayFallbackDates().check_out_date,
                          }
                    )}
                    className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-all hover:opacity-90 border"
                    style={{
                      backgroundColor: MAJOR_GOLD_SOFT,
                      borderColor: MAJOR_GOLD,
                      color: THEME.dark
                    }}
                  >
                    <Bell className="w-3.5 h-3.5" style={{ color: MAJOR_GOLD }} />
                    <span>{t.contactReception}</span>
                    <ChevronRight className="w-3.5 h-3.5 opacity-60" />
                  </button>
                </div>
              )}
            </div>
          );
        })}
        {loading && (
          <div className="flex justify-start">
            <div className="text-gray-900 rounded-lg px-4 py-2" style={{ backgroundColor: THEME.light, border: '1px solid rgba(0,0,0,0.06)' }}>
              <div className="flex space-x-2">
                <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
                <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
                <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
              </div>
            </div>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>

<form onSubmit={sendMessage} className="p-3" style={{ backgroundColor: 'rgba(255,255,255,0.85)' }}>
        <div className="flex gap-2 items-center">
          <button
            type="button"
            onClick={() => setShowUserInfoModal(true)}
            className="flex-shrink-0 w-9 h-9 rounded-full flex items-center justify-center border border-black/10 hover:border-opacity-60 transition-colors"
            style={{ backgroundColor: conversation?.user_name ? MAJOR_GOLD_SOFT : 'transparent', borderColor: conversation?.user_name ? MAJOR_GOLD : undefined }}
            title={t.profileButtonTitle}
          >
            <User className="w-4 h-4" style={{ color: conversation?.user_name ? MAJOR_GOLD : '#6B7280' }} />
          </button>
          <input
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder={t.placeholder}
            className="chat-major-focus flex-1 px-4 py-2 border border-black/10 rounded-lg"
            disabled={loading}
          />
          <button
            type="submit"
            disabled={loading || !input.trim()}
            className="flex-shrink-0 p-2 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-opacity hover:opacity-90"
            style={{ backgroundColor: MAJOR_GOLD, color: THEME.dark }}
          >
            <Send className="w-5 h-5" />
          </button>
        </div>
        {!conversation?.user_name && (
          <button
            type="button"
            onClick={() => setShowUserInfoModal(true)}
            className="mt-2 w-full text-xs text-center py-1.5 rounded-lg transition-colors hover:opacity-80"
            style={{ color: MAJOR_GOLD, backgroundColor: MAJOR_GOLD_SOFT }}
          >
            {t.profilePrompt}
          </button>
        )}
      </form>

      {receptionForm?.show && (
        <div className="absolute inset-0 bg-black/50 backdrop-blur-sm flex items-end justify-center z-20">
          <div
            className="w-full bg-white rounded-t-2xl shadow-2xl overflow-hidden"
            style={{ maxHeight: '90%' }}
          >
            <div
              className="px-5 py-4 flex items-center justify-between text-white"
              style={{ backgroundColor: THEME.bg }}
            >
              <div className="flex items-center gap-2.5">
                <Bell className="w-4 h-4" style={{ color: MAJOR_GOLD }} />
                <span className="font-semibold text-sm">{t.receptionFormTitle}</span>
              </div>
              <button
                onClick={() => setReceptionForm(null)}
                className="hover:bg-white/10 p-1.5 rounded-lg transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="overflow-y-auto p-5" style={{ maxHeight: 'calc(90vh - 60px)' }}>
              {receptionForm.submitted ? (
                <div className="flex flex-col items-center text-center py-4 gap-4">
                  <div
                    className="w-14 h-14 rounded-full flex items-center justify-center"
                    style={{ backgroundColor: MAJOR_GOLD_SOFT }}
                  >
                    <CheckCircle className="w-7 h-7" style={{ color: MAJOR_GOLD }} />
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 text-base mb-2">{t.receptionFormSuccess}</h4>
                    <p className="text-gray-500 text-sm leading-relaxed">{t.receptionFormSuccessDesc}</p>
                  </div>
                  <button
                    onClick={() => setReceptionForm(null)}
                    className="mt-2 w-full py-2.5 rounded-xl text-sm font-semibold text-white transition-opacity hover:opacity-90"
                    style={{ backgroundColor: THEME.bg }}
                  >
                    {t.cancel}
                  </button>
                </div>
              ) : (
                <div className="space-y-4">
                  <div
                    className="rounded-xl p-3.5 text-sm leading-relaxed"
                    style={{ backgroundColor: MAJOR_GOLD_SOFT, color: THEME.dark }}
                  >
                    {t.receptionFormDelayNotice}
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1.5">{t.receptionFormName}</label>
                    <input
                      type="text"
                      value={receptionForm.guest_name}
                      onChange={e => setReceptionForm(prev => prev ? { ...prev, guest_name: e.target.value } : null)}
                      placeholder={t.receptionFormNamePlaceholder}
                      className="chat-major-focus w-full px-3.5 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none"
                    />
                  </div>

                  <div>
                    <p className="block text-xs font-semibold text-gray-700 mb-1.5">{t.receptionFormStatus}</p>
                    <div className="grid grid-cols-2 gap-2">
                      <button
                        type="button"
                        onClick={() => setReceptionForm(prev => prev ? {
                          ...prev, isResident: false, room_number: '', check_in_date: '', check_out_date: ''
                        } : null)}
                        className="flex items-center justify-center gap-1.5 py-2 px-2 rounded-lg border text-xs font-medium transition-all"
                        style={!receptionForm.isResident
                          ? { backgroundColor: MAJOR_GOLD_SOFT, borderColor: MAJOR_GOLD, color: THEME.dark }
                          : { borderColor: 'rgba(0,0,0,0.1)', color: '#6B7280' }
                        }
                      >
                        {t.receptionFormExternalVisitor}
                      </button>
                      <button
                        type="button"
                        onClick={() => setReceptionForm(prev => prev ? { ...prev, isResident: true } : null)}
                        className="flex items-center justify-center gap-1.5 py-2 px-2 rounded-lg border text-xs font-medium transition-all"
                        style={receptionForm.isResident
                          ? { backgroundColor: MAJOR_GOLD_SOFT, borderColor: MAJOR_GOLD, color: THEME.dark }
                          : { borderColor: 'rgba(0,0,0,0.1)', color: '#6B7280' }
                        }
                      >
                        <Home className="w-3 h-3" />
                        {t.receptionFormHotelResident}
                      </button>
                    </div>
                  </div>

                  {receptionForm.isResident && (
                    <div
                      className="rounded-xl p-3.5 space-y-2.5"
                      style={{ backgroundColor: MAJOR_GOLD_SOFT, border: `1px solid ${MAJOR_GOLD}` }}
                    >
                      <div className="flex items-center gap-1.5 mb-0.5">
                        <Home className="w-3 h-3" style={{ color: MAJOR_GOLD }} />
                        <span className="text-xs font-semibold text-gray-700 uppercase tracking-wide">{t.receptionFormStay}</span>
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">{t.receptionFormRoomNumber}</label>
                        <input
                          type="text"
                          value={receptionForm.room_number}
                          onChange={e => setReceptionForm(prev => prev ? { ...prev, room_number: e.target.value } : null)}
                          placeholder={t.receptionFormRoomPlaceholder}
                          className="chat-major-focus w-full px-3 py-2 border bg-white border-black/10 rounded-lg text-sm focus:outline-none"
                        />
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <label className="flex items-center gap-1 text-xs font-medium text-gray-600 mb-1">
                            <Calendar className="w-3 h-3" /> {t.receptionFormCheckIn}
                          </label>
                          <input
                            type="date"
                            value={receptionForm.check_in_date}
                            onChange={e => setReceptionForm(prev => prev ? { ...prev, check_in_date: e.target.value } : null)}
                            className="chat-major-focus w-full px-3 py-2 border bg-white border-black/10 rounded-lg text-sm focus:outline-none"
                          />
                        </div>
                        <div>
                          <label className="flex items-center gap-1 text-xs font-medium text-gray-600 mb-1">
                            <Calendar className="w-3 h-3" /> {t.receptionFormCheckOut}
                          </label>
                          <input
                            type="date"
                            value={receptionForm.check_out_date}
                            onChange={e => setReceptionForm(prev => prev ? { ...prev, check_out_date: e.target.value } : null)}
                            className="chat-major-focus w-full px-3 py-2 border bg-white border-black/10 rounded-lg text-sm focus:outline-none"
                          />
                        </div>
                      </div>
                    </div>
                  )}

                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                      {t.receptionFormEmail}
                    </label>
                    <input
                      type="email"
                      value={receptionForm.email}
                      onChange={e => setReceptionForm(prev => prev ? { ...prev, email: e.target.value, emailError: '' } : null)}
                      placeholder={t.receptionFormEmailPlaceholder}
                      className="chat-major-focus w-full px-3.5 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none"
                    />
                    {receptionForm.emailError && (
                      <p className="mt-1 text-xs text-red-500">{receptionForm.emailError}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                      {t.receptionFormMessage}
                    </label>
                    <textarea
                      value={receptionForm.message}
                      onChange={e => setReceptionForm(prev => prev ? { ...prev, message: e.target.value } : null)}
                      placeholder={t.receptionFormMessagePlaceholder}
                      rows={4}
                      className="chat-major-focus w-full px-3.5 py-2.5 border border-gray-200 rounded-xl text-sm resize-none focus:outline-none"
                    />
                  </div>

                  <div className="flex gap-3 pt-1">
                    <button
                      onClick={() => setReceptionForm(null)}
                      className="flex-1 py-2.5 border border-gray-200 text-gray-600 rounded-xl text-sm font-medium hover:bg-gray-50 transition-colors"
                    >
                      {t.receptionFormCancel}
                    </button>
                    <button
                      onClick={submitReceptionMessage}
                      disabled={receptionForm.submitting || !receptionForm.message.trim()}
                      className="flex-1 py-2.5 rounded-xl text-sm font-semibold text-white transition-opacity hover:opacity-90 disabled:opacity-50 flex items-center justify-center gap-2"
                      style={{ backgroundColor: THEME.bg }}
                    >
                      {receptionForm.submitting ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <Send className="w-4 h-4" />
                      )}
                      {t.receptionFormSubmit}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {showUserInfoModal && (
        <div className="absolute inset-0 bg-black/50 backdrop-blur-sm flex items-end justify-center z-20">
          <div className="w-full bg-white rounded-t-2xl shadow-2xl overflow-hidden" style={{ maxHeight: '90%' }}>
            <div className="px-5 py-4 flex items-center justify-between text-white" style={{ backgroundColor: THEME.bg }}>
              <div className="flex items-center gap-2.5">
                <User className="w-4 h-4" />
                <span className="font-semibold text-sm">{t.profileTitle}</span>
              </div>
              <button onClick={() => setShowUserInfoModal(false)} className="hover:bg-white/10 p-1.5 rounded-lg transition-colors">
                <X className="w-4 h-4" />
              </button>
            </div>
            <UserInfoForm
              conversation={conversation}
              onSave={async (info) => { await handleSaveUserInfo(info); setShowUserInfoModal(false); }}
              onCancel={() => setShowUserInfoModal(false)}
              THEME={THEME}
              MAJOR_GOLD={MAJOR_GOLD}
              MAJOR_GOLD_SOFT={MAJOR_GOLD_SOFT}
              t={t}
            />
          </div>
        </div>
      )}
    </div>
  );
}

interface UserInfoFormProps {
  conversation: ChatConversation | null;
  onSave: (info: { user_name: string; user_email: string; user_phone: string; room_number: string; check_in_date: string; check_out_date: string }) => Promise<void>;
  onCancel: () => void;
  THEME: { bg: string; dark: string; medium: string; light: string };
  MAJOR_GOLD: string;
  MAJOR_GOLD_SOFT: string;
  t: ReturnType<typeof getT>;
}

function UserInfoForm({ conversation, onSave, onCancel, THEME, MAJOR_GOLD, MAJOR_GOLD_SOFT, t }: UserInfoFormProps) {
  const [form, setForm] = useState({
    user_name: conversation?.user_name || '',
    user_email: conversation?.user_email || '',
    user_phone: conversation?.user_phone || '',
    room_number: conversation?.room_number || '',
    check_in_date: conversation?.check_in_date || getStayFallbackDates().check_in_date,
    check_out_date: conversation?.check_out_date || getStayFallbackDates().check_out_date,
  });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async () => {
    setSaving(true);
    await onSave(form);
    setSaving(false);
  };

  return (
    <div className="overflow-y-auto p-5 space-y-4" style={{ maxHeight: 'calc(90vh - 60px)' }}>
      <div>
        <label className="block text-xs font-semibold text-gray-700 mb-1.5">{t.profileName}</label>
        <input type="text" value={form.user_name} onChange={e => setForm(p => ({ ...p, user_name: e.target.value }))}
          placeholder={t.profileNamePlaceholder} className="w-full px-3.5 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none" />
      </div>
      <div>
        <label className="block text-xs font-semibold text-gray-700 mb-1.5">{t.profileEmail}</label>
        <input type="email" value={form.user_email} onChange={e => setForm(p => ({ ...p, user_email: e.target.value }))}
          placeholder={t.receptionFormEmailPlaceholder} className="w-full px-3.5 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none" />
      </div>
      <div>
        <label className="block text-xs font-semibold text-gray-700 mb-1.5">{t.profilePhone}</label>
        <input type="tel" value={form.user_phone} onChange={e => setForm(p => ({ ...p, user_phone: e.target.value }))}
          placeholder={t.profilePhonePlaceholder} className="w-full px-3.5 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none" />
      </div>
      <div className="rounded-xl p-3.5 space-y-2.5" style={{ backgroundColor: MAJOR_GOLD_SOFT, border: `1px solid ${MAJOR_GOLD}` }}>
        <div className="flex items-center gap-1.5">
          <Home className="w-3 h-3" style={{ color: MAJOR_GOLD }} />
          <span className="text-xs font-semibold text-gray-700 uppercase tracking-wide">{t.profileStay}</span>
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">{t.profileRoomNumber}</label>
          <input type="text" value={form.room_number} onChange={e => setForm(p => ({ ...p, room_number: e.target.value }))}
            placeholder={t.profileRoomPlaceholder} className="w-full px-3 py-2 border bg-white border-black/10 rounded-lg text-sm focus:outline-none" />
        </div>
        <div className="grid grid-cols-2 gap-2">
          <div>
            <label className="flex items-center gap-1 text-xs font-medium text-gray-600 mb-1"><Calendar className="w-3 h-3" /> {t.profileCheckIn}</label>
            <input type="date" value={form.check_in_date} onChange={e => setForm(p => ({ ...p, check_in_date: e.target.value }))}
              className="w-full px-3 py-2 border bg-white border-black/10 rounded-lg text-sm focus:outline-none" />
          </div>
          <div>
            <label className="flex items-center gap-1 text-xs font-medium text-gray-600 mb-1"><Calendar className="w-3 h-3" /> {t.profileCheckOut}</label>
            <input type="date" value={form.check_out_date} onChange={e => setForm(p => ({ ...p, check_out_date: e.target.value }))}
              className="w-full px-3 py-2 border bg-white border-black/10 rounded-lg text-sm focus:outline-none" />
          </div>
        </div>
      </div>
      <div className="flex gap-3 pt-1">
        <button onClick={onCancel} className="flex-1 py-2.5 border border-gray-200 text-gray-600 rounded-xl text-sm font-medium hover:bg-gray-50 transition-colors">
          {t.profileCancel}
        </button>
        <button onClick={handleSubmit} disabled={saving}
          className="flex-1 py-2.5 rounded-xl text-sm font-semibold text-white transition-opacity hover:opacity-90 disabled:opacity-50 flex items-center justify-center gap-2"
          style={{ backgroundColor: THEME.bg }}>
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
          {t.profileSave}
        </button>
      </div>
    </div>
  );
}
